import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { 
  TrendingUp, TrendingDown, Loader2, Clock, MapPin, Timer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to get flag emoji
const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

const TransferPerformance = ({ token }) => {
  const [transferTimes, setTransferTimes] = useState(null);
  const [countryStats, setCountryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let timeUrl = `${API}/admin/analytics/transfer-times?year=${selectedYear}`;
      if (selectedMonth) timeUrl += `&month=${selectedMonth}`;
      
      const [timesRes, countryRes] = await Promise.all([
        axios.get(timeUrl, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/analytics/transfers-by-country?year=${selectedYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTransferTimes(timesRes.data);
      setCountryStats(countryRes.data);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast.error('Erreur lors du chargement des données de performance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(value);
  };

  const formatDuration = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    } else if (hours < 24) {
      return `${hours.toFixed(1)} h`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days} j ${remainingHours.toFixed(0)} h`;
    }
  };

  const months = [
    { value: '', label: 'Toute l\'année' },
    { value: '1', label: 'Janvier' },
    { value: '2', label: 'Février' },
    { value: '3', label: 'Mars' },
    { value: '4', label: 'Avril' },
    { value: '5', label: 'Mai' },
    { value: '6', label: 'Juin' },
    { value: '7', label: 'Juillet' },
    { value: '8', label: 'Août' },
    { value: '9', label: 'Septembre' },
    { value: '10', label: 'Octobre' },
    { value: '11', label: 'Novembre' },
    { value: '12', label: 'Décembre' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label>Année:</Label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border rounded-md"
              >
                {[2024, 2025, 2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Mois:</Label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Statistics Cards */}
      {transferTimes && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Temps moyen</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatDuration(transferTimes.statistics.average_hours)}
                  </p>
                  <p className="text-xs text-blue-600">
                    {transferTimes.statistics.average_days.toFixed(1)} jours
                  </p>
                </div>
                <Timer className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600 font-medium">Temps médian</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {formatDuration(transferTimes.statistics.median_hours)}
                  </p>
                  <p className="text-xs text-purple-600">
                    {transferTimes.statistics.median_days.toFixed(1)} jours
                  </p>
                </div>
                <Clock className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Plus rapide</p>
                  <p className="text-2xl font-bold text-green-700">
                    {formatDuration(transferTimes.statistics.min_hours)}
                  </p>
                  <p className="text-xs text-green-600">
                    {transferTimes.statistics.min_days.toFixed(1)} jours
                  </p>
                </div>
                <TrendingDown className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600 font-medium">Plus lent</p>
                  <p className="text-2xl font-bold text-amber-700">
                    {formatDuration(transferTimes.statistics.max_hours)}
                  </p>
                  <p className="text-xs text-amber-600">
                    {transferTimes.statistics.max_days.toFixed(1)} jours
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Country Statistics */}
      {countryStats && countryStats.by_country.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-brand-600" />
              Transferts par pays - {selectedYear}
            </CardTitle>
            <CardDescription>
              Répartition géographique des transactions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Pays</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Total</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Complétés</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">En cours</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Annulés</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Volume</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {countryStats.by_country.map((country, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getFlagEmoji(country.country)}</span>
                          {country.country_name}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-semibold">{country.total_transactions}</td>
                      <td className="py-3 px-4 text-right text-green-600">{country.completed}</td>
                      <td className="py-3 px-4 text-right text-yellow-600">{country.pending}</td>
                      <td className="py-3 px-4 text-right text-red-600">{country.cancelled}</td>
                      <td className="py-3 px-4 text-right">{formatCurrency(country.total_volume)}</td>
                      <td className="py-3 px-4 text-right text-green-600">{formatCurrency(country.total_commission)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr className="font-bold">
                    <td className="py-3 px-4">Total</td>
                    <td className="py-3 px-4 text-right">{countryStats.totals.total_transactions}</td>
                    <td className="py-3 px-4 text-right text-green-600">{countryStats.totals.completed}</td>
                    <td className="py-3 px-4 text-right text-yellow-600">{countryStats.totals.pending}</td>
                    <td className="py-3 px-4 text-right text-red-600">{countryStats.totals.cancelled}</td>
                    <td className="py-3 px-4 text-right">{formatCurrency(countryStats.totals.total_volume)}</td>
                    <td className="py-3 px-4 text-right text-green-600">{formatCurrency(countryStats.totals.total_commission)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TransferPerformance;
